<template>
  <app-custom-dialog
    :value="value"
    title="compare_excel_with_data"
    width="90vw"
    dialog-height="85vh"
    :footer="true"
    :close-btn="true"
    :flex-layout="true"
    cancel-text="cancel"
    submit-text="confirm_import"
    :disable-submit="totalErrors > 0"
    :loading="confirming"
    @input="$emit('input', $event)"
    @submit="onConfirm"
  >
    <div slot="activator">
      <div v-if="summary" class="compare-summary">
        <div class="compare-summary-stat">
          <div class="compare-summary-value">{{ distinctProductCount }}</div>
          <div class="compare-summary-label">{{ $t("product") }}</div>
        </div>
        <div class="compare-summary-stat">
          <div class="compare-summary-value">{{ summary.sku_total }}</div>
          <div class="compare-summary-label">SKU</div>
        </div>
        <div class="compare-summary-stat">
          <div class="compare-summary-value">
            {{ summary.ingredient_total }}
          </div>
          <div class="compare-summary-label">{{ $t("ingredient") }}</div>
        </div>
        <div
          class="compare-summary-stat compare-summary-stat--error"
          v-if="totalErrors > 0"
        >
          <div class="compare-summary-value">{{ totalErrors }}</div>
          <div class="compare-summary-label">{{ $t("error") }}</div>
        </div>
      </div>

      <v-tabs v-model="activeTab" grow class="mt-2">
        <v-tab>{{ $t("product") }}</v-tab>
        <v-tab>SKU</v-tab>
        <v-tab>{{ $t("ingredient") }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-data-table
            :headers="productHeaders"
            :items="productRows"
            :items-per-page="15"
            disable-sort
            dense
            class="mt-3"
          >
            <template #[`item.errors`]="{ item }">
              <v-chip
                v-for="(err, i) in item.errors"
                :key="i"
                x-small
                color="red"
                dark
                class="mr-1 mb-1"
              >
                {{ $t(err) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item>
          <v-data-table
            :headers="skuHeaders"
            :items="skuRows"
            :items-per-page="15"
            disable-sort
            dense
            class="mt-3"
          >
            <template #[`item.price`]="{ item }">
              {{ formatNumber(item.price) }}
            </template>
            <template #[`item.cost`]="{ item }">
              {{ formatNumber(item.cost) }}
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item>
          <v-data-table
            :headers="ingredientHeaders"
            :items="ingredientRows"
            :items-per-page="15"
            disable-sort
            dense
            class="mt-3"
          >
            <template #[`item.quantity`]="{ item }">
              {{ formatNumber(item.quantity) }}
            </template>
            <template #[`item.cost`]="{ item }">
              {{ formatNumber(item.cost) }}
            </template>
            <template #[`item.errors`]="{ item }">
              <v-chip
                v-for="(err, i) in item.errors"
                :key="i"
                x-small
                color="red"
                dark
                class="mr-1 mb-1"
              >
                {{ $t(err) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </app-custom-dialog>
</template>

<script>
export default {
  name: "ProductRecipeCompareDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    compareData: {
      type: Object,
      default: null,
    },
    // Parent-controlled: true while the real product_recipe_import request
    // is in flight, so the confirm button shows a spinner and can't be
    // double-clicked. The parent owns this because the actual API call
    // (and the file it re-uploads) lives in the parent, not this dialog.
    confirming: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    activeTab: 0,
  }),
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return num.toLocaleString();
    },
    onConfirm() {
      this.$emit("confirm", this.compareData);
    },
  },
  computed: {
    productRows() {
      return this.compareData?.product_rows ?? [];
    },
    // summary.product_total (from the backend) counts "product can edit"
    // rows, i.e. one per OPTION AXIS — a product with 2 axes (Color, size)
    // counts as 2, which reads as "2 products" in the summary tile even
    // though it's really 1 product with 2 axis rows. Count distinct
    // product_code here instead for the tile shown above the tabs; the
    // per-row table below is unaffected and still correctly shows one row
    // per axis.
    distinctProductCount() {
      return new Set(
        this.productRows
          .map((row) => (row.product_code || "").trim().toLowerCase())
          .filter((code) => code !== "")
      ).size;
    },
    skuRows() {
      const rows = this.compareData?.sku_rows ?? [];
      const ingredientRows = this.compareData?.ingredient_rows ?? [];

      // Build map of combined SKU -> total cost from ingredient_rows
      const costBySku = {};
      ingredientRows.forEach((row) => {
        const key = (row.sku_combined || "").trim().toLowerCase();
        if (key && row.cost !== null && row.cost !== undefined && !Number.isNaN(Number(row.cost))) {
          costBySku[key] = (costBySku[key] || 0) + Number(row.cost);
        }
      });

      return rows.map((row) => {
        const key = (row.combined || "").trim().toLowerCase();
        const hasIngredients = key in costBySku;
        // A SKU with recipe rows: show the real recipe-cost total (matches
        // what the backend will actually save — see
        // ProductRecipeImportBuilder::resolveVariantCost()). A SKU with NO
        // recipe rows: `row.cost` is the user's own manually-typed value
        // from "option can edit"!ຕົ້ນທຶນ (that's exactly the case the
        // backend uses it for) — keep it instead of blanking it out.
        return {
          ...row,
          cost: hasIngredients ? costBySku[key] : row.cost ?? null,
        };
      });
    },
    ingredientRows() {
      return this.compareData?.ingredient_rows ?? [];
    },
    summary() {
      return this.compareData?.summary ?? null;
    },
    totalErrors() {
      if (!this.summary) return 0;
      return (
        (this.summary.product_errors ?? 0) +
        (this.summary.ingredient_errors ?? 0)
      );
    },
    productHeaders() {
      return [
        { text: this.$t("product"), value: "product_display" },
        { text: this.$t("option_name"), value: "option_name" },
        { text: this.$t("option_values"), value: "option_values" },
        { text: this.$t("error"), value: "errors", sortable: false },
      ];
    },
    skuHeaders() {
      return [
        { text: this.$t("product"), value: "product_code" },
        { text: this.$t("option_axes"), value: "option_axes" },
        { text: "SKU", value: "sku" },
        { text: this.$t("product") + " - SKU", value: "combined" },
        { text: this.$t("price"), value: "price", align: "end" },
        { text: this.$t("cost"), value: "cost", align: "end" },
      ];
    },
    ingredientHeaders() {
      return [
        { text: this.$t("option") + " - SKU", value: "sku_combined" },
        { text: this.$t("ingredient"), value: "ingredient_name" },
        { text: this.$t("unit"), value: "unit_name" },
        { text: this.$t("quantity"), value: "quantity", align: "end" },
        { text: this.$t("cost"), value: "cost", align: "end" },
        { text: this.$t("error"), value: "errors", sortable: false },
      ];
    },
  },
};
</script>

<style scoped>
.compare-summary {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.compare-summary-stat {
  flex: 1 1 100px;
  background: #f4f7fb;
  border-radius: 10px;
  padding: 10px 8px;
  text-align: center;
}
.compare-summary-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}
.compare-summary-label {
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
}
.compare-summary-stat--error {
  background: #fdeceb;
  color: #842029;
}
::v-deep tbody td {
  font-size: 16px !important;
}
::v-deep
  .v-data-table--dense
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td,
::v-deep
  .v-data-table--dense
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > td,
::v-deep
  .v-data-table--dense
  > .v-data-table__wrapper
  > table
  > tfoot
  > tr
  > td {
  height: 38px;
}
::v-deep .v-chip .v-chip__content {
  align-items: center;
  display: inline-flex;
  height: 100%;
  max-width: 100%;
  font-size: 11px;
}
/* Stick the pagination footer to the bottom of the scrollable body
   so it stays visible whether data is sparse or overflowing. */
::v-deep .v-data-footer {
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 2;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
